import React, { useState, useEffect } from 'react';
import { Instrument } from './InstrumentSelector';
import { formatTuningDisplay } from '../utils/tuningHelpers';
import CustomTuningForm from './CustomTuningForm';
import useLocalStorage from '../hooks/useLocalStorage';
import { FaPlus, FaTimes } from 'react-icons/fa';

export interface CustomTuning {
  name: string;
  notes: string[];
  description: string;
  instrumentName: string;
}

interface TuningSelectorProps {
  tuning: string;
  setTuning: (tuning: string) => void;
  instrument: Instrument;
  customTunings: CustomTuning[];
  setCustomTunings: (tunings: CustomTuning[]) => void;
}

const TuningSelector: React.FC<TuningSelectorProps> = ({ tuning, setTuning, instrument, customTunings, setCustomTunings }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  
  const selectedTuning = instrument.tunings.find(tuningOption => tuningOption.name === tuning) || 
                        customTunings.find(t => t.name === tuning && t.instrumentName === instrument.name);

  useEffect(() => {
    if (tuning && instrument) {
      localStorage.setItem(`${instrument.name}_lastTuning`, tuning);
    }
  }, [tuning, instrument]);

  const handleSaveCustomTuning = (newTuning: CustomTuning) => {
    setCustomTunings([...customTunings, newTuning]);
    setTuning(newTuning.name);
    setIsModalOpen(false);
  };

  const handleTuningChange = (newTuning: string) => {
    if (newTuning === "add-custom-tuning") {
      setIsModalOpen(true);
    } else {
      setTuning(newTuning);
    }
  };

  const handleDeleteCustomTuning = () => {
    setCustomTunings(customTunings.filter(t => t.name !== tuning || t.instrumentName !== instrument.name));
    setTuning(instrument.defaultTuning); // Reset to default tuning or handle as needed
    setIsDeleteConfirmOpen(false);
  };

  const instrumentCustomTunings = customTunings.filter(t => t.instrumentName === instrument.name);

  return (
    <div className="form-control">
      <label className="label">
        <span className="label-text">Tuning:</span>
      </label>
      <div className="flex gap-2">
        <select
          className="select select-bordered flex-grow"
          value={tuning}
          onChange={(e) => handleTuningChange(e.target.value)}
        >
          {instrument.tunings.map((tuningOption, index) => (
            <option key={index} value={tuningOption.name}>
              {formatTuningDisplay(instrument, tuningOption.notes)} - {tuningOption.name}
            </option>
          ))}
          
            <optgroup label="Custom Tunings">
              {instrumentCustomTunings.map((tuningOption, index) => (
                <option key={`custom-${index}`} value={tuningOption.name}>
                  {formatTuningDisplay(instrument, tuningOption.notes)} - {tuningOption.name}
                </option>
              ))}
              <option key="add-custom-tuning" value="add-custom-tuning">
                *** Add Custom Tuning ***
              </option>
            </optgroup>
          
        </select>
        <button 
          className="btn btn-square btn-primary" 
          onClick={() => setIsModalOpen(true)}
          title="Add Custom Tuning"
        >
          <FaPlus />
        </button>
        {selectedTuning && customTunings.some(t => t.name === tuning && t.instrumentName === instrument.name) && (
          <button 
            className="btn btn-square"
            onClick={() => setIsDeleteConfirmOpen(true)}
            title="Delete Custom Tuning"
          >
            <FaTimes />
          </button>
        )}
      </div>
      {selectedTuning && (
        <label className="label">
          <span className="label-text-alt">{selectedTuning.description}</span>
        </label>
      )}

      {isModalOpen && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-4">Add Custom Tuning</h3>
            <CustomTuningForm
              instrument={instrument}
              onSave={handleSaveCustomTuning}
              onCancel={() => setIsModalOpen(false)}
              initialTuning={selectedTuning}
            />
          </div>
          <div className="modal-backdrop" onClick={() => setIsModalOpen(false)}>
            <button className="cursor-default">close</button>
          </div>
        </div>
      )}

      {isDeleteConfirmOpen && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-4">Confirm Deletion</h3>
            <p>Are you sure you want to delete this custom tuning?</p>
            <div className="modal-action">
              <button className="btn" onClick={() => setIsDeleteConfirmOpen(false)}>Cancel</button>
              <button className="btn btn-error" onClick={handleDeleteCustomTuning}>Delete</button>
            </div>
          </div>
          <div className="modal-backdrop" onClick={() => setIsDeleteConfirmOpen(false)}>
            <button className="cursor-default">close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TuningSelector;